import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { DeviceIDBadge } from '../../../shared/components/common/Badges';
import ModalCloseButton from '../../../shared/components/modals/ModalCloseButton';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import './DeviceQRCode.css';

function DeviceQRCode({ device, onClose }) {
  const [qrUrl, setQrUrl] = useState(null);
  const [isGeneratingToken, setIsGeneratingToken] = useState(true);
  const [tokenError, setTokenError] = useState(null);
  const [copiedFeedback, setCopiedFeedback] = useState(false);

  // QR Error Correction Level - Configurable for different document styles
  // 'L' = Low (7%) - Cleanest, most white space
  // 'M' = Medium (15%) - Balanced, matches sample documents
  // 'Q' = Quartile (25%) - High redundancy
  // 'H' = High (30%) - Most robust, very dense
  const [qrErrorCorrectionLevel, setQrErrorCorrectionLevel] = useState('M');

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    const generateToken = async () => {
      try {
        setIsGeneratingToken(true);
        setTokenError(null);

        const deviceIdentifier = device.device_identifier || device.device_id || '';
        if (!deviceIdentifier) {
          setTokenError('Device identifier not found');
          console.error('Device identifier not found');
          setIsGeneratingToken(false);
          return;
        }

        const response = await fetch(
          API_ENDPOINTS.DEVICES.QR_TOKEN(deviceIdentifier),
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem('token')}`,
            },
          }
        );

        if (!response.ok) {
          const errorMsg = `Failed to generate QR token: ${response.statusText}`;
          setTokenError(errorMsg);
          throw new Error(errorMsg);
        }

        const data = await response.json();
        if (data.success && data.qr_url) {
          setQrUrl(data.qr_url);
          setTokenError(null);
        } else {
          const errorMsg = data.error || 'Failed to generate QR token';
          setTokenError(errorMsg);
          throw new Error(errorMsg);
        }
      } catch (err) {
        console.error('Error generating QR token:', err);
        setTokenError(err.message || 'Failed to generate QR code. Please try again.');
      } finally {
        setIsGeneratingToken(false);
      }
    };

    generateToken();
  }, [device]);

  const getQRToken = () => qrUrl || '';

  const handlePrint = () => {
    const appName = process.env.REACT_APP_APP_NAME || 'DevicePulse';
    const deviceId = device.device_identifier || device.device_id || 'unknown';
    const deviceName = device.name ? device.name.replace(/\s+/g, '_') : 'device';
    const fileName = `${appName}_${deviceId}_${deviceName}`;

    const originalTitle = document.title;
    document.title = fileName;
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
    }, 500);
  };

  const triggerFeedback = () => {
    setCopiedFeedback(true);
    setTimeout(() => setCopiedFeedback(false), 2000);
  };

  const handleCopyUrl = async () => {
    if (!qrUrl) return;

    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(qrUrl);
        triggerFeedback();
      } catch (err) {
        console.error('Modern copy failed', err);
      }
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = qrUrl;
      textArea.style.fontSize = '12pt';
      Object.assign(textArea.style, {
        position: 'fixed',
        left: '-9999px',
        top: '0',
        readonly: true,
      });
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, 999999);
      try {
        if (document.execCommand('copy')) triggerFeedback();
      } catch (err) {
        console.error('Fallback copy failed', err);
      }
      document.body.removeChild(textArea);
    }
  };

  const qualityHint =
    qrErrorCorrectionLevel === 'L'
      ? 'Cleanest'
      : qrErrorCorrectionLevel === 'M'
        ? 'Balanced'
        : qrErrorCorrectionLevel === 'Q'
          ? 'Robust'
          : 'Most robust';

  const renderShell = ({ body, footer = null }) => (
    <div className="qr-code-modal">
      <div className="qr-overlay" onClick={onClose} aria-hidden="true" />
      <div
        className="qr-container"
        role="dialog"
        aria-modal="true"
        aria-labelledby="device-qr-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="qr-header">
          <h2 id="device-qr-modal-title">Device QR Code</h2>
          <ModalCloseButton onClick={onClose} title="Close" />
        </div>
        <div className="qr-body">{body}</div>
        {footer}
      </div>
    </div>
  );

  if (isGeneratingToken) {
    return renderShell({
      body: (
        <div className="qr-status-panel">
          <div className="spinner" aria-hidden="true" />
          <p className="qr-status-title">Generating QR code…</p>
          <p className="qr-status-hint">This may take a moment.</p>
        </div>
      ),
    });
  }

  if (tokenError) {
    return renderShell({
      body: (
        <div className="qr-status-panel">
          <p className="qr-status-error">⚠️ {tokenError}</p>
        </div>
      ),
      footer: (
        <div className="qr-footer">
          <button type="button" className="action-btn action-secondary" onClick={onClose}>
            <span className="action-text">Close</span>
          </button>
        </div>
      ),
    });
  }

  return renderShell({
    body: (
      <>
        {/* Print Layout - matches bulk QR label card (QRBatchRenderer) */}
        <div className="qr-print-layout">
          <div className="qr-print-device-id">
            {device.device_identifier || device.device_id}
          </div>

          <div className="qr-print-code-wrap">
            <QRCodeSVG
              id="device-qr-code"
              value={getQRToken()}
              size={240}
              level={qrErrorCorrectionLevel}
              includeMargin={false}
              className="qr-code-svg"
            />
          </div>

          <div className="qr-print-info">
            <table className="qr-print-info-table">
              <colgroup>
                <col className="qr-print-label-col" />
                <col />
              </colgroup>
              <tbody>
                <tr>
                  <td className="qr-print-label">Equipment:</td>
                  <td className="qr-print-value">{device.name}</td>
                </tr>
                <tr>
                  <td className="qr-print-label">Dept:</td>
                  <td className="qr-print-value">{device.department || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="qr-print-label">Model:</td>
                  <td className="qr-print-value">{device.model}</td>
                </tr>
                <tr>
                  <td className="qr-print-label">SN:</td>
                  <td className="qr-print-value">{device.serial_number}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="qr-screen-layout">
          <div className="qr-device-info">
            <h3 className="qr-device-name">{device.name}</h3>
            <p className="qr-model">
              {device.manufacturer} — {device.model}
            </p>
            <p className="qr-serial-secondary">SN {device.serial_number}</p>
          </div>

          <div className="qr-code-display">
            <div className="qr-device-id-badge">
              <DeviceIDBadge deviceId={device.device_identifier || device.device_id} size="small" />
            </div>
            <QRCodeSVG
              id="device-qr-code-display"
              value={getQRToken()}
              size={176}
              level={qrErrorCorrectionLevel}
              includeMargin={false}
              className="qr-code-svg"
            />
            <button
              type="button"
              className={`action-btn action-secondary qr-copy-link-btn${copiedFeedback ? ' copied' : ''}`}
              onClick={handleCopyUrl}
              title={copiedFeedback ? 'Copied' : 'Copy link'}
              aria-label={copiedFeedback ? 'Link copied' : 'Copy link'}
            >
              <span className="action-icon" aria-hidden="true">{copiedFeedback ? '✓' : '📋'}</span>
            </button>
          </div>

          <div className="qr-quality-section">
            <div className="qr-quality-header">
              <label className="qr-quality-label" htmlFor="qr-quality-select">
                QR Quality
              </label>
              <span className="qr-quality-badge">{qualityHint}</span>
            </div>
            <select
              id="qr-quality-select"
              value={qrErrorCorrectionLevel}
              onChange={(e) => setQrErrorCorrectionLevel(e.target.value)}
              className="qr-quality-select"
              title="Change QR code error correction level"
            >
              <option value="L">L — Low (7%) — Cleanest</option>
              <option value="M">M — Medium (15%) — Balanced</option>
              <option value="Q">Q — Quartile (25%) — Robust</option>
              <option value="H">H — High (30%) — Most Robust</option>
            </select>
          </div>

          <p className="qr-instructions">
            Scan to view this device or report an issue. Guests can report without signing in.
          </p>
        </div>
      </>
    ),
    footer: (
      <div className="qr-footer">
        <button type="button" className="action-btn action-primary" onClick={handlePrint}>
          <span className="action-icon" aria-hidden="true">🖨️</span>
          <span className="action-text">Print QR Label</span>
        </button>
      </div>
    ),
  });
}

export default DeviceQRCode;
